import { FilterOperator } from './filter.types';
import type {
  IActiveFilter,
  IFilterableColumn,
  IFilterPayload,
} from './filter.types';
import { OPERATOR_CONFIG, OPERATORS_BY_TYPE } from './filter.const';

/**
 * Get default value for an operator.
 * In / NIn use an empty array (multi-value); everything else uses ''.
 */
export const getDefaultValue = (operator: FilterOperator): string | string[] =>
  operator === FilterOperator.In || operator === FilterOperator.NIn ? [] : '';

export const getDefaultOperator = (column: IFilterableColumn): FilterOperator =>
  (column.operators ?? OPERATORS_BY_TYPE[column.type])[0] ?? FilterOperator.Eq;

export const emptyFilterFor = (column: IFilterableColumn): IActiveFilter => {
  const operator = getDefaultOperator(column);
  return { column, operator, value: getDefaultValue(operator) };
};

export const buildDefaultFilters = (
  columns: IFilterableColumn[]
): IActiveFilter[] => columns.filter((c) => c.isDefault).map(emptyFilterFor);

/**
 * Serialize a filter value to the backend's string form. Arrays (In / NIn)
 * are wrapped as `[v1,v2,…]`; single values pass through unchanged.
 */
export const serializeFilterValue = (value: string | string[]): string =>
  Array.isArray(value) ? `[${value.join(',')}]` : value;

/**
 * Inverse of `serializeFilterValue`: turn a stored payload back into the
 * array form expected by multi-value operators. Tolerates the
 * `[v1,v2,…]` wrapper as well as bare comma-separated input.
 */
export const parseFilterValueToArray = (raw: string): string[] => {
  if (!raw) return [];
  const trimmed = raw.trim();
  const body =
    trimmed.startsWith('[') && trimmed.endsWith(']')
      ? trimmed.slice(1, -1)
      : trimmed;
  return body
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
};

/**
 * Transform a single active filter to backend payload format.
 * For In/NIn: array value is stringified to "[v1,v2,…]".
 */
export const toFilterPayload = (filter: IActiveFilter): IFilterPayload => ({
  column: filter.column.key,
  operation: filter.operator,
  value: serializeFilterValue(filter.value),
  type: filter.column.type,
});

export const parseFilterClauses = (raw: string): IFilterPayload[] => {
  try {
    const parsed = JSON.parse(raw) as {
      filters?: IFilterPayload[];
      f?: IFilterPayload[];
    };
    if (Array.isArray(parsed.filters)) return parsed.filters;
    if (Array.isArray(parsed.f)) return parsed.f;
    return [];
  } catch {
    return [];
  }
};

export const stringifyFilterClauses = (clauses: IFilterPayload[]): string =>
  JSON.stringify({ filters: clauses });

/**
 * True when a filter has no value to send. Default-filter chips that the user
 * hasn't filled in yet land here and must be skipped before serialization.
 */
const isFilterEmpty = (filter: IActiveFilter): boolean => {
  if (!OPERATOR_CONFIG[filter.operator]?.requiresValue) return false;
  if (Array.isArray(filter.value)) return filter.value.length === 0;
  return filter.value == null || String(filter.value).trim() === '';
};

/**
 * Serialize active filters to JSON string for backend. Filters with no value
 * (e.g. unconfigured default-filter chips) are skipped so the backend behaves
 * as if they were never selected. Returns an empty string when nothing
 * meaningful remains, so call sites can skip the query param entirely.
 */
export const serializeFilters = (filters: IActiveFilter[]): string => {
  const payload = filters.filter((f) => !isFilterEmpty(f)).map(toFilterPayload);
  return payload.length === 0 ? '' : stringifyFilterClauses(payload);
};
